import { useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import type { ProductListFacets } from "@/lib/api-client";

export interface FilterPanelProps {
  facets: ProductListFacets;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Porté depuis apps/web/components/product/FilterPanel.tsx. Seul changement :
 * `usePathname`/`useRouter` de next/navigation → `useSearchParams()` de
 * react-router-dom (dont le setter conserve déjà le pathname courant).
 */
export function FilterPanel({ facets, isOpen, onClose }: FilterPanelProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedBrands = useMemo(
    () => new Set((searchParams.get("brand") ?? "").split(",").filter(Boolean)),
    [searchParams],
  );
  const selectedAttributes = useMemo(() => new Set(searchParams.getAll("attribute")), [searchParams]);
  const selectedRating = searchParams.get("rating");
  const inStockOnly = searchParams.get("inStock") === "true";

  const [priceMin, setPriceMin] = useState(searchParams.get("priceMin") ?? "");
  const [priceMax, setPriceMax] = useState(searchParams.get("priceMax") ?? "");

  function pushParams(mutate: (params: URLSearchParams) => void) {
    const params = new URLSearchParams(searchParams);
    mutate(params);
    setSearchParams(params);
  }

  function toggleBrand(slug: string) {
    pushParams((params) => {
      const next = new Set(selectedBrands);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      if (next.size > 0) params.set("brand", [...next].join(","));
      else params.delete("brand");
    });
  }

  function toggleAttribute(token: string) {
    pushParams((params) => {
      params.delete("attribute");
      const next = new Set(selectedAttributes);
      if (next.has(token)) next.delete(token);
      else next.add(token);
      for (const value of next) params.append("attribute", value);
    });
  }

  function toggleRating(minRating: number) {
    pushParams((params) => {
      if (selectedRating === String(minRating)) params.delete("rating");
      else params.set("rating", String(minRating));
    });
  }

  function toggleInStock() {
    pushParams((params) => {
      if (inStockOnly) params.delete("inStock");
      else params.set("inStock", "true");
    });
  }

  function applyPriceRange(e: FormEvent) {
    e.preventDefault();
    pushParams((params) => {
      if (priceMin) params.set("priceMin", priceMin);
      else params.delete("priceMin");
      if (priceMax) params.set("priceMax", priceMax);
      else params.delete("priceMax");
    });
  }

  function clearAll() {
    pushParams((params) => {
      for (const key of ["brand", "attribute", "priceMin", "priceMax", "inStock", "rating"]) {
        params.delete(key);
      }
    });
    setPriceMin("");
    setPriceMax("");
  }

  const hasActiveFilters =
    selectedBrands.size > 0 ||
    selectedAttributes.size > 0 ||
    inStockOnly ||
    Boolean(selectedRating) ||
    Boolean(searchParams.get("priceMin")) ||
    Boolean(searchParams.get("priceMax"));

  const content = (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-base font-semibold text-navy">Filtres</h2>
        {hasActiveFilters ? (
          <button type="button" onClick={clearAll} className="font-body text-xs text-muted underline hover:text-navy">
            Tout effacer
          </button>
        ) : null}
      </div>

      {facets.brands.length > 0 ? (
        <fieldset className="space-y-2">
          <legend className="font-body text-xs font-semibold uppercase tracking-wide text-navy">Marque</legend>
          {facets.brands.map((b) => (
            <label
              key={b.id}
              className={["flex items-center justify-between gap-2 font-body text-sm", b.count === 0 ? "text-line" : "text-ink"].join(" ")}
            >
              <span className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedBrands.has(b.slug)}
                  disabled={b.count === 0 && !selectedBrands.has(b.slug)}
                  onChange={() => toggleBrand(b.slug)}
                  className="h-4 w-4 border-line accent-navy"
                />
                {b.name}
              </span>
              <span className="text-xs text-muted">{b.count}</span>
            </label>
          ))}
        </fieldset>
      ) : null}

      {facets.price ? (
        <fieldset className="space-y-2">
          <legend className="font-body text-xs font-semibold uppercase tracking-wide text-navy">Prix (FCFA)</legend>
          <form onSubmit={applyPriceRange} className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              inputMode="numeric"
              placeholder={String(facets.price.min)}
              value={priceMin}
              onChange={(e) => setPriceMin(e.target.value)}
              className="w-full rounded-sm border border-line px-2 py-1.5 text-sm"
              aria-label="Prix minimum"
            />
            <span className="text-muted">–</span>
            <input
              type="number"
              min={0}
              inputMode="numeric"
              placeholder={String(facets.price.max)}
              value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)}
              className="w-full rounded-sm border border-line px-2 py-1.5 text-sm"
              aria-label="Prix maximum"
            />
            <button
              type="submit"
              className="shrink-0 rounded-sm border border-navy px-2 py-1.5 font-body text-xs uppercase text-navy"
            >
              OK
            </button>
          </form>
        </fieldset>
      ) : null}

      {facets.attributes.map((attr) => (
        <fieldset key={attr.code} className="space-y-2">
          <legend className="font-body text-xs font-semibold uppercase tracking-wide text-navy">{attr.label}</legend>
          <div className="flex flex-wrap gap-2">
            {attr.values.map((v) => {
              const token = `${attr.code}:${v.normalizedValue}`;
              const active = selectedAttributes.has(token);
              const disabled = v.count === 0 && !active;
              return (
                <button
                  key={v.attributeValueId}
                  type="button"
                  disabled={disabled}
                  onClick={() => toggleAttribute(token)}
                  title={`${v.label} (${v.count})`}
                  className={[
                    "flex items-center gap-1.5 rounded-sm border px-2 py-1 font-body text-xs",
                    active ? "border-navy bg-navy text-cream" : "border-line bg-white text-ink",
                    disabled ? "cursor-not-allowed opacity-40" : "hover:border-navy",
                  ].join(" ")}
                >
                  {v.hexColor ? (
                    <span
                      aria-hidden="true"
                      className="h-3 w-3 rounded-full border border-line"
                      style={{ backgroundColor: v.hexColor }}
                    />
                  ) : null}
                  {v.label}
                </button>
              );
            })}
          </div>
        </fieldset>
      ))}

      {facets.rating.length > 0 ? (
        <fieldset className="space-y-2">
          <legend className="font-body text-xs font-semibold uppercase tracking-wide text-navy">Note</legend>
          {facets.rating.map((r) => (
            <label key={r.minRating} className="flex items-center gap-2 font-body text-sm text-ink">
              <input
                type="checkbox"
                checked={selectedRating === String(r.minRating)}
                onChange={() => toggleRating(r.minRating)}
                className="h-4 w-4 border-line accent-navy"
              />
              <span aria-hidden="true" className="text-gold">
                {"★".repeat(r.minRating)}
                {"☆".repeat(5 - r.minRating)}
              </span>
              <span>&amp; plus ({r.count})</span>
            </label>
          ))}
        </fieldset>
      ) : null}

      {facets.availability.inStock > 0 ? (
        <fieldset>
          <label className="flex items-center gap-2 font-body text-sm text-ink">
            <input
              type="checkbox"
              checked={inStockOnly}
              onChange={toggleInStock}
              className="h-4 w-4 border-line accent-navy"
            />
            En stock uniquement ({facets.availability.inStock})
          </label>
        </fieldset>
      ) : null}
    </div>
  );

  return (
    <>
      {/* Colonne latérale — VVS_MODELE_RESPONSIVE.md §6.5 : lg et au-delà. */}
      <aside className="hidden lg:col-span-3 lg:block">{content}</aside>

      {/* Tiroir plein écran — sous lg. */}
      {isOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Fermer les filtres"
            onClick={onClose}
            className="absolute inset-0 bg-navy/40"
          />
          <div className="absolute inset-y-0 right-0 flex w-full max-w-sm flex-col bg-cream p-4 shadow-xl">
            <div className="flex items-center justify-between border-b border-line pb-3">
              <p className="font-display text-lg font-semibold text-navy">Filtres</p>
              <button type="button" onClick={onClose} aria-label="Fermer" className="text-xl text-navy">
                &times;
              </button>
            </div>
            <div className="flex-1 overflow-y-auto py-4">{content}</div>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 rounded-sm border border-gold bg-gold px-6 py-3 font-body text-sm font-medium uppercase tracking-wide text-navy"
            >
              Voir les produits
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
